import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TestResult } from '../../../shared/test.types';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { ANIMAL_CLUSTERS, WORD_LISTS, WORD_LISTS_HE } from '../../sharp-v2.types';
import { SharpAudioService } from '../../services/sharp-audio.service';
import { SharpScoringService } from '../../services/sharp-scoring.service';
import { SharpStateService } from '../../services/sharp-state.service';
import { TranslationService } from '../../../../services/translation.service';

type Phase = 'intro' | 'active' | 'done';

interface Flower {
  id: number;
  word: string;
  x: number;
  z: number;
  rotate: number;
  anim: string;
  dur: string;
  html: SafeHtml;
}

interface Ephemeral {
  id: number;
  html: SafeHtml;
}

const DURATION_SEC = 30;

@Component({
  selector: 'app-word-garden',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './word-garden.component.html',
  styleUrls: ['./word-garden.component.scss'],
})
export class WordGardenComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() testId = 'word-garden';
  @Input() durationSec = DURATION_SEC;
  @Output() testComplete = new EventEmitter<TestResult>();
  @ViewChild('field', { static: false }) fieldRef?: ElementRef<HTMLDivElement>;
  @ViewChild('demoField', { static: false })
  demoFieldRef?: ElementRef<HTMLDivElement>;
  @ViewChild('inputEl', { static: false })
  inputRef?: ElementRef<HTMLInputElement>;

  phase: Phase = 'intro';
  category: keyof typeof WORD_LISTS = 'Animals';
  timeLeft = DURATION_SEC;
  inputValue = '';
  feedback = '';
  feedbackKind: 'info' | 'warn' | 'error' = 'info';

  // ACTIVE garden state
  flowers: Flower[] = [];
  ephemerals: Ephemeral[] = [];
  words: string[] = [];
  timestamps: number[] = [];
  repetitions = 0;
  counterPop = false;

  // Demo (intro screen)
  demoFlowers: Flower[] = [];
  demoInputText = '';
  demoInputFlash = false;
  demoExplainVisible = false;

  // Field progressive styling
  fieldBackground: string | null = null;
  fieldBorderColor: string | null = null;
  fieldBoxShadow: string | null = null;

  private startTime = 0;
  /** page hidden mid-round: the clock is stopped, not run out (brief 3.3) */
  paused = false;
  private pausedAt = 0;
  private interruptions = 0;
  private wallStart = 0;
  private timerHandle: any = null;
  private feedbackTimer: any = null;
  private demoTimers: any[] = [];
  private demoRunning = false;
  private ephemeralCleanup: any[] = [];
  private idCounter = 0;
  /** keyboard up during the round: the game is pinned to the visible part of the screen */
  kbPinned = false;
  vvTop = 0;
  vvHeight = 0;
  private vvRaf = 0;

  constructor(
    private audio: SharpAudioService,
    private scoring: SharpScoringService,
    private state: SharpStateService,
    private sanitizer: DomSanitizer,
    private translation: TranslationService
  ) {}

  /** Category word set for the active language (Hebrew speech/typing must match
   *  Hebrew words — the English sets can never match a he-IL transcript). */
  private get wordList(): Set<string> {
    const lists =
      this.translation.getCurrentLanguage() === 'he' ? WORD_LISTS_HE : WORD_LISTS;
    return lists[this.category] || WORD_LISTS[this.category];
  }

  /**
   * The shape a word is compared in, for duplicate detection only (correction
   * brief 19.3). Typing chair and then chairs used to grow two flowers and count
   * twice, which inflates a number the report then shows as fluency. Validity is
   * untouched: this decides whether a word was ALREADY said, never whether it
   * belongs to the category.
   *
   * Deliberately shallow. A real stemmer would collapse words that are genuinely
   * different answers, and over-collapsing silently costs her a point she
   * earned, which is worse than the occasional missed duplicate.
   */
  private duplicateKey(word: string): string {
    const w = word.trim().toLowerCase();
    if (this.translation.getCurrentLanguage() === 'he') {
      // Hebrew plurals: masculine ים, feminine ות. Stripping leaves the stem in
      // its medial form, so the last letter goes back to its final form -
      // otherwise שולחנות reduces to שולחנ and never matches שולחן.
      for (const suffix of ['ים', 'ות']) {
        if (w.length > suffix.length + 1 && w.endsWith(suffix)) {
          return this.withFinalLetter(w.slice(0, -suffix.length));
        }
      }
      return w;
    }
    if (w.length > 4 && w.endsWith('ies')) return w.slice(0, -3) + 'y';
    // knife/knives, leaf/leaves, shelf/shelves: both sides reduce to the f stem
    if (w.length > 4 && w.endsWith('ves')) return w.slice(0, -3) + 'f';
    if (w.length > 3 && w.endsWith('fe')) return w.slice(0, -1);
    if (w.length > 4 && w.endsWith('es')) {
      const stem = w.slice(0, -2);
      // only where the es is really the plural: glasses -> glass, boxes -> box
      if (/(s|x|z|ch|sh)$/.test(stem)) return stem;
    }
    // glass and bus keep their double s; only a single trailing s is a plural
    if (w.length > 3 && w.endsWith('s') && !w.endsWith('ss')) return w.slice(0, -1);
    return w;
  }

  private withFinalLetter(stem: string): string {
    const finals: Record<string, string> = {
      'כ': 'ך',
      'מ': 'ם',
      'נ': 'ן',
      'פ': 'ף',
      'צ': 'ץ',
    };
    const last = stem.slice(-1);
    return finals[last] ? stem.slice(0, -1) + finals[last] : stem;
  }

  ngOnInit(): void {
    this.wallStart = Date.now();
    // Step2 change7: rotate to a category the user hasn't had recently (falls back
    // to random when there's no history).
    const cats = Object.keys(WORD_LISTS);
    this.category = this.state.pickWordCategory(cats) as keyof typeof WORD_LISTS;
    this.timeLeft = this.durationSec;
    document.addEventListener('visibilitychange', this.onVisibility);
    window.visualViewport?.addEventListener('resize', this.onViewport);
    window.visualViewport?.addEventListener('scroll', this.onViewport);
  }

  ngAfterViewInit(): void {
    // Kick off demo after a brief delay so layout settles.
    setTimeout(() => this.runDemo(), 700);
  }

  private readonly onVisibility = () => {
    if (document.visibilityState === 'hidden') this.pause();
  };

  /**
   * Correction brief 3.3: this is a timed fluency round, and a call or an app
   * switch used to burn her seconds while she was away, which reads as fewer
   * words. The clock now stops on a hidden page and she carries on with the time
   * she had left; the words already typed are kept. The word timestamps are
   * shifted by the pause so the gap does not show up as a slow interval.
   */
  private pause(): void {
    if (this.phase !== 'active' || this.paused) return;
    if (this.timerHandle) clearInterval(this.timerHandle);
    this.timerHandle = null;
    this.pausedAt = performance.now();
    this.paused = true;
    this.interruptions++;
  }

  resumeAfterInterrupt(): void {
    if (!this.paused || this.phase !== 'active') return;
    this.startTime += performance.now() - this.pausedAt;
    this.paused = false;
    this.runClock();
    setTimeout(() => this.inputRef?.nativeElement.focus(), 100);
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.visualViewport?.removeEventListener('resize', this.onViewport);
    window.visualViewport?.removeEventListener('scroll', this.onViewport);
    if (this.vvRaf) cancelAnimationFrame(this.vvRaf);
    this.demoRunning = false;
    this.clearTimers();
    this.ephemeralCleanup.forEach((t) => clearTimeout(t));
  }

  private clearTimers(): void {
    if (this.timerHandle) clearInterval(this.timerHandle);
    if (this.feedbackTimer) clearTimeout(this.feedbackTimer);
    this.timerHandle = null;
    this.feedbackTimer = null;
    this.demoTimers.forEach((t) => {
      clearTimeout(t);
      clearInterval(t);
    });
    this.demoTimers = [];
  }

  get exampleKey(): string {
    if (this.category === 'Animals')
      return 'cognitive.sharpV2.wordGarden.examplesAnimals';
    if (this.category === 'Food')
      return 'cognitive.sharpV2.wordGarden.examplesFood';
    return 'cognitive.sharpV2.wordGarden.examplesHousehold';
  }

  get categoryKey(): string {
    if (this.category === 'Animals')
      return 'cognitive.sharpV2.wordGarden.categoryAnimals';
    if (this.category === 'Food')
      return 'cognitive.sharpV2.wordGarden.categoryFood';
    return 'cognitive.sharpV2.wordGarden.categoryHousehold';
  }

  /**
   * With the keyboard open, iOS Safari scrolls the page to the input and the
   * timer, category and count go off the top. While the round runs, pin the
   * whole game to the visual viewport (what is actually visible above the
   * keyboard): header on top, input row on the bottom edge, the garden takes
   * whatever height is left. Layout only, the clock and scoring are untouched.
   */
  private onViewport = (): void => {
    if (this.vvRaf) return;
    this.vvRaf = requestAnimationFrame(() => {
      this.vvRaf = 0;
      const vv = window.visualViewport;
      if (!vv) return;
      const covered = window.innerHeight - vv.height - vv.offsetTop;
      const pin = this.phase === 'active' && covered > 120;
      this.kbPinned = pin;
      this.vvTop = pin ? vv.offsetTop : 0;
      this.vvHeight = pin ? vv.height : 0;
      if (pin && window.scrollY !== 0 && vv.offsetTop === 0) window.scrollTo(0, 0);
    });
  };

  /** keeps the input focused (and the keyboard up) when + is tapped */
  keepFocus(ev: Event): void {
    ev.preventDefault();
  }

  get timerPct(): number {
    return (this.timeLeft / this.durationSec) * 100;
  }

  start(): void {
    this.demoRunning = false;
    this.phase = 'active';
    this.startTime = performance.now();
    this.timeLeft = this.durationSec;
    this.runClock();
    setTimeout(() => this.inputRef?.nativeElement.focus(), 200);
  }

  private runClock(): void {
    if (this.timerHandle) clearInterval(this.timerHandle);
    this.timerHandle = setInterval(() => {
      this.timeLeft--;
      if (this.timeLeft <= 0) {
        this.clearTimers();
        this.endTest();
      }
    }, 1000);
  }

  onInputChange(): void {
    if (this.inputValue.includes(',') || /\s\s/.test(this.inputValue)) {
      this.submitWord();
    }
  }

  onKey(ev: KeyboardEvent): void {
    if (ev.key === 'Enter' || ev.key === ',' || ev.key === ' ') {
      ev.preventDefault();
      this.submitWord();
    }
  }

  onFieldClick(): void {
    this.inputRef?.nativeElement.focus();
  }

  trackById = (_: number, item: { id: number }): number => item.id;

  submitWord(): void {
    if (this.paused) return;
    const parts = this.inputValue
      .split(/[,;\s]+/)
      .map((w) => w.trim().toLowerCase())
      .filter((w) => w.length > 0);
    this.inputValue = '';
    if (!parts.length) {
      this.setFeedback('cognitive.sharpV2.wordGarden.typeFirst', 'warn');
      this.inputRef?.nativeElement.focus();
      return;
    }
    this.handleWords(parts);
    this.inputRef?.nativeElement.focus();
  }

  private handleWords(parts: string[]): void {
    let lastMsg = '';
    let lastKind: 'info' | 'warn' | 'error' = 'info';
    const list = this.wordList;
    for (const raw of parts) {
      const key = this.duplicateKey(raw);
      if (this.words.some((w) => this.duplicateKey(w) === key)) {
        this.repetitions++;
        lastMsg = 'cognitive.sharpV2.wordGarden.alreadySaid';
        lastKind = 'warn';
        continue;
      }
      let valid = list.has(raw) || this.inCompactList(raw, list);
      if (!valid) {
        for (const w of list) {
          if (this.scoring.fuzzyMatch(raw, w)) {
            valid = true;
            break;
          }
        }
      }
      if (valid) {
        this.words.push(raw);
        this.timestamps.push(performance.now() - this.startTime);
        this.audio.word();
        this.popCounter();
        this.bloomFlower(raw);
        lastMsg = '';
      } else {
        lastMsg = 'cognitive.sharpV2.wordGarden.notInCategory';
        lastKind = 'error';
      }
    }
    if (lastMsg) this.setFeedback(lastMsg, lastKind);
    else this.feedback = '';
  }

  /** Matches spacing/hyphen/plural variants of list entries: "hotdogs" and
   *  "hot-dog" both match "hot dog", "icecream" matches "ice cream". */
  private inCompactList(raw: string, list: Set<string>): boolean {
    const compact = (s: string) => s.toLowerCase().replace(/[\s\-']/g, '');
    const w = compact(raw);
    const forms = [w];
    if (w.endsWith('es') && w.length > 4) forms.push(w.slice(0, -2));
    if (w.endsWith('s') && w.length > 3) forms.push(w.slice(0, -1));
    for (const entry of list) {
      if (forms.includes(compact(entry))) return true;
    }
    return false;
  }

  private popCounter(): void {
    this.counterPop = false;
    // force animation restart
    setTimeout(() => (this.counterPop = true), 0);
    setTimeout(() => (this.counterPop = false), 320);
  }

  private setFeedback(
    key: string,
    kind: 'info' | 'warn' | 'error' = 'info'
  ): void {
    this.feedback = key;
    this.feedbackKind = kind;
    if (this.feedbackTimer) clearTimeout(this.feedbackTimer);
    this.feedbackTimer = setTimeout(() => (this.feedback = ''), 2000);
  }

  /** Time's up — score the result. */
  private endTest(): void {
    this.finish();
  }

  // ============================
  // FLOWER RENDERING (ported VERBATIM from ref bloomFlower)
  // ============================
  private bloomFlower(word: string): void {
    const fieldEl = this.fieldRef?.nativeElement;
    const fw = fieldEl?.offsetWidth || 320;
    const fh = fieldEl?.offsetHeight || 260;
    const n = this.flowers.length;

    const PALETTES = [
      { base: '#FF1493', light: '#FF6EB4', dark: '#C71585' },
      { base: '#AA00FF', light: '#D580FF', dark: '#7B00B5' },
      { base: '#6C3CE0', light: '#A07BFF', dark: '#4400AA' },
      { base: '#2979FF', light: '#82B1FF', dark: '#0D47A1' },
      { base: '#00E5CC', light: '#80FFF0', dark: '#009688' },
      { base: '#FF3D00', light: '#FF8A65', dark: '#BF2C00' },
      { base: '#FFAB00', light: '#FFD54F', dark: '#FF8F00' },
      { base: '#FF1744', light: '#FF6B7A', dark: '#C41230' },
      { base: '#E040FB', light: '#F08FFF', dark: '#AA00FF' },
      { base: '#FF4081', light: '#FF80AB', dark: '#F50057' },
      { base: '#651FFF', light: '#A47BFF', dark: '#4A00C8' },
      { base: '#FF6D00', light: '#FFB74D', dark: '#E65100' },
    ];
    const pal =
      PALETTES[(word.charCodeAt(0) + n * 3) % PALETTES.length];
    const color = pal.base,
      colorL = pal.light,
      colorD = pal.dark;

    const cols = Math.max(3, Math.floor(fw / 55));
    const col = n % cols;
    const row = Math.floor(n / cols);
    const x = 10 + col * ((fw - 20) / cols) + this.scoring.randInt(-12, 12);
    const heightClass = n % 3;
    const baseH = [45, 75, 110][heightClass];
    const stemH = baseH + Math.random() * 40 + row * 8;
    const types = [
      'peony',
      'tulip',
      'dahlia',
      'poppy',
      'magnolia',
      'bell',
      'orchid',
      'lotus',
      'wildrose',
      'cosmos',
    ];
    const type = types[(word.length + word.charCodeAt(0) + n) % types.length];
    const anims = [
      'sharp-bloom-spring',
      'sharp-bloom-bounce',
      'sharp-bloom-spin',
      'sharp-bloom-pop',
      'sharp-bloom-drop',
      'sharp-bloom-swirl',
    ];
    const anim = anims[n % anims.length];
    const dur = (0.3 + Math.random() * 0.3).toFixed(2);
    const swaySpeed = (1.5 + Math.random() * 3).toFixed(1);
    const sway = `animation:sharp-petal-sway ${swaySpeed}s ease-in-out infinite;animation-delay:${(Math.random() * 2).toFixed(1)}s;`;
    const clampedX = Math.max(5, Math.min(fw - 45, x));

    let headHTML = '';
    const sz = Math.min(110, 45 + word.length * 7 + Math.random() * 22);
    const glow = `filter:drop-shadow(0 0 ${sz * 0.3}px ${color}90) drop-shadow(0 0 ${sz * 0.15}px ${color}50)`;
    const randInt = (a: number, b: number) => this.scoring.randInt(a, b);

    if (type === 'peony') {
      let petals = '';
      const layers = 4;
      for (let l = 0; l < layers; l++) {
        const pc = 5 + l * 2;
        const lsz = sz * (0.3 + l * 0.2);
        for (let i = 0; i < pc; i++) {
          const a = i * (360 / pc) + l * 12 + randInt(-8, 8);
          const clr = l < 2 ? color : l === 2 ? colorL : colorL;
          petals += `<div style="position:absolute;width:${lsz * 0.45}px;height:${lsz * 0.65}px;background:linear-gradient(${a}deg,${clr},${l > 1 ? colorL : color});border-radius:50%;top:50%;left:50%;transform:translate(-50%,-${55 + l * 12}%) rotate(${a}deg);transform-origin:center bottom;opacity:${(0.6 + l * 0.1).toFixed(1)};box-shadow:inset 0 -2px 4px ${colorD}30"></div>`;
        }
      }
      headHTML = `<div style="position:relative;width:${sz * 1.8}px;height:${sz * 1.8}px;${sway};${glow}">${petals}<div style="position:absolute;width:${sz * 0.2}px;height:${sz * 0.2}px;background:radial-gradient(#FFF9C4,#FFD54F);border-radius:50%;top:48%;left:50%;transform:translate(-50%,-50%);z-index:5;box-shadow:0 0 ${sz * 0.15}px #FFD54F80"></div></div>`;
    } else if (type === 'tulip') {
      const h = sz * 1.3;
      const w = sz * 0.65;
      headHTML = `<div style="${sway};${glow}"><div style="width:${w}px;height:${h}px;background:linear-gradient(160deg,${colorD},${color},${colorL});border-radius:${w * 0.5}px ${w * 0.5}px ${w * 0.12}px ${w * 0.12}px;box-shadow:0 0 ${sz * 0.2}px ${color}60,inset ${w * 0.08}px 0 ${w * 0.15}px rgba(255,255,255,.2);position:relative;overflow:hidden"><div style="position:absolute;top:0;left:0;width:100%;height:100%;background:linear-gradient(to right,rgba(255,255,255,.12),transparent 40%,rgba(0,0,0,.06))"></div></div></div>`;
    } else if (type === 'dahlia') {
      let petals = '';
      const totalP = 14 + randInt(0, 8);
      for (let i = 0; i < totalP; i++) {
        const a = i * 137.5;
        const dist = sz * 0.05 + i * (sz * 0.03);
        const pw = sz * 0.18 + i * 0.5;
        const ph = sz * 0.3 + i * 0.8;
        const dx = Math.cos((a * Math.PI) / 180) * dist;
        const dy = Math.sin((a * Math.PI) / 180) * dist;
        const op = (0.5 + (i / totalP) * 0.5).toFixed(2);
        petals += `<div style="position:absolute;width:${pw}px;height:${ph}px;background:linear-gradient(to top,${color},${i > totalP / 2 ? colorL : color});border-radius:50%;top:${50 - (dy / sz) * 50}%;left:${50 + (dx / sz) * 50}%;transform:translate(-50%,-50%) rotate(${a}deg);opacity:${op};box-shadow:inset 0 -1px 3px ${colorD}20"></div>`;
      }
      headHTML = `<div style="position:relative;width:${sz * 1.7}px;height:${sz * 1.7}px;${sway};${glow}">${petals}<div style="position:absolute;width:${sz * 0.18}px;height:${sz * 0.18}px;background:radial-gradient(#FFF9C4,#FFD54F);border-radius:50%;top:50%;left:50%;transform:translate(-50%,-50%);z-index:5;box-shadow:0 0 ${sz * 0.12}px #FFD54F80"></div></div>`;
    } else if (type === 'poppy') {
      let petals = '';
      const pc = 4 + randInt(0, 2);
      for (let i = 0; i < pc; i++) {
        const a = i * (360 / pc) + randInt(-10, 10);
        const pw = sz * 0.55;
        const ph = sz * 0.65;
        petals += `<div style="position:absolute;width:${pw}px;height:${ph}px;background:radial-gradient(${colorL} 10%,${color} 50%,${colorD});border-radius:50%;top:50%;left:50%;transform:translate(-50%,-100%) rotate(${a}deg);transform-origin:center bottom;box-shadow:0 0 ${sz * 0.1}px ${color}40"></div>`;
      }
      headHTML = `<div style="position:relative;width:${sz * 1.6}px;height:${sz * 1.6}px;${sway};${glow}">${petals}<div style="position:absolute;width:${sz * 0.3}px;height:${sz * 0.3}px;background:radial-gradient(#1B1B1B,#333);border-radius:50%;top:50%;left:50%;transform:translate(-50%,-50%);z-index:1;box-shadow:0 0 6px rgba(0,0,0,.3)"></div></div>`;
    } else if (type === 'magnolia') {
      let petals = '';
      const pc = 6 + randInt(0, 3);
      for (let i = 0; i < pc; i++) {
        const a = i * (360 / pc) + randInt(-6, 6);
        const pw = sz * 0.3;
        const ph = sz * 0.7;
        petals += `<div style="position:absolute;width:${pw}px;height:${ph}px;background:linear-gradient(to top,${colorD},${color},${colorL});border-radius:50% 50% 20% 20%;top:50%;left:50%;transform:translate(-50%,-95%) rotate(${a}deg);transform-origin:center bottom;box-shadow:0 0 ${sz * 0.08}px ${color}50"></div>`;
      }
      headHTML = `<div style="position:relative;width:${sz * 1.7}px;height:${sz * 1.7}px;${sway};${glow}">${petals}<div style="position:absolute;width:${sz * 0.22}px;height:${sz * 0.22}px;background:radial-gradient(#FFF9C4,#FFCC80,${color});border-radius:50%;top:50%;left:50%;transform:translate(-50%,-50%);z-index:2;box-shadow:0 0 ${sz * 0.15}px ${colorL}60"></div></div>`;
    } else if (type === 'bell') {
      const bw = sz * 0.6;
      const bh = sz * 1.2;
      headHTML = `<div style="${sway};${glow}"><div style="width:${bw}px;height:${bh}px;background:linear-gradient(170deg,${colorD},${color},${colorL});border-radius:${bw * 0.1}px ${bw * 0.1}px ${bw * 0.4}px ${bw * 0.4}px;box-shadow:0 0 ${bw * 0.25}px ${color}50,inset ${bw * 0.06}px 0 ${bw * 0.12}px rgba(255,255,255,.18);position:relative"><div style="position:absolute;bottom:-4px;left:50%;transform:translateX(-50%);width:7px;height:7px;background:radial-gradient(#FFF9C4,#FFD54F);border-radius:50%;box-shadow:0 0 8px #FFD54F80"></div></div></div>`;
    } else if (type === 'orchid') {
      let petals = '';
      const shapes: [number, number, number][] = [
        [1, 0.65, 35],
        [0.65, 1, -35],
        [0.55, 1.1, 0],
        [1.1, 0.5, 80],
        [0.5, 0.8, 130],
      ];
      shapes.forEach(([w, h, r], i) => {
        const oclr = i < 3 ? color : colorL;
        petals += `<div style="position:absolute;width:${sz * w * 0.55}px;height:${sz * h * 0.55}px;background:linear-gradient(${r}deg,${oclr},${i < 2 ? colorD : colorL});border-radius:50%;top:50%;left:50%;transform:translate(-50%,-50%) rotate(${r + randInt(-15, 15)}deg);box-shadow:0 0 ${sz * 0.08}px ${color}40"></div>`;
      });
      headHTML = `<div style="position:relative;width:${sz * 1.7}px;height:${sz * 1.7}px;${sway};${glow}">${petals}<div style="position:absolute;width:${sz * 0.2}px;height:${sz * 0.24}px;background:radial-gradient(#FFF9C4,${colorL});border-radius:35%;top:50%;left:50%;transform:translate(-50%,-50%);z-index:2;box-shadow:0 0 ${sz * 0.12}px ${colorL}60"></div></div>`;
    } else if (type === 'lotus') {
      let petals = '';
      for (let l = 0; l < 4; l++) {
        const pc = 5 + l * 2;
        const lsz = sz * (0.45 + l * 0.2);
        for (let i = 0; i < pc; i++) {
          const a = i * (360 / pc) + l * 15;
          const clr = l < 2 ? colorD : l === 2 ? color : colorL;
          petals += `<div style="position:absolute;width:${lsz * 0.28}px;height:${lsz * 0.5}px;background:linear-gradient(to top,${clr},${colorL});border-radius:50% 50% 6% 6%;top:50%;left:50%;transform:translate(-50%,-${55 + l * 13}%) rotate(${a}deg);transform-origin:center bottom;opacity:${(0.5 + l * 0.14).toFixed(1)};box-shadow:0 0 ${lsz * 0.06}px ${clr}40"></div>`;
        }
      }
      headHTML = `<div style="position:relative;width:${sz * 1.8}px;height:${sz * 1.8}px;${sway};${glow}">${petals}<div style="position:absolute;width:${sz * 0.26}px;height:${sz * 0.26}px;background:radial-gradient(#FFF9C4,#FFD54F,#FFB300);border-radius:50%;top:46%;left:50%;transform:translate(-50%,-50%);z-index:5;box-shadow:0 0 ${sz * 0.2}px #FFD54F90"></div></div>`;
    } else if (type === 'wildrose') {
      let petals = '';
      const pc = 5;
      for (let i = 0; i < pc; i++) {
        const a = i * (360 / pc) + randInt(-8, 8);
        const pw = sz * 0.5;
        const ph = sz * 0.55;
        petals += `<div style="position:absolute;width:${pw}px;height:${ph}px;background:radial-gradient(ellipse at 50% 70%,${colorL} 15%,${color} 55%,${colorD} 95%);border-radius:50% 50% 45% 45%;top:50%;left:50%;transform:translate(-50%,-95%) rotate(${a}deg);transform-origin:center bottom;box-shadow:inset 0 ${ph * 0.1}px ${ph * 0.15}px rgba(255,255,255,.2),0 0 ${sz * 0.08}px ${color}40"></div>`;
      }
      headHTML = `<div style="position:relative;width:${sz * 1.6}px;height:${sz * 1.6}px;${sway};${glow}">${petals}<div style="position:absolute;width:${sz * 0.28}px;height:${sz * 0.28}px;background:radial-gradient(#FFF9C4,#FFD54F,#FFA000);border-radius:50%;top:50%;left:50%;transform:translate(-50%,-50%);z-index:2;box-shadow:0 0 ${sz * 0.15}px #FFD54F90"></div></div>`;
    } else {
      let petals = '';
      const pc = 8;
      for (let i = 0; i < pc; i++) {
        const a = i * (360 / pc);
        const pw = sz * 0.22;
        const ph = sz * 0.55;
        petals += `<div style="position:absolute;width:${pw}px;height:${ph}px;background:linear-gradient(to top,${colorD},${color});border-radius:50% 50% 30% 30%;top:50%;left:50%;transform:translate(-50%,-100%) rotate(${a}deg);transform-origin:center bottom;box-shadow:0 0 ${sz * 0.06}px ${color}40"></div>`;
      }
      headHTML = `<div style="position:relative;width:${sz * 1.6}px;height:${sz * 1.6}px;${sway};${glow}">${petals}<div style="position:absolute;width:${sz * 0.24}px;height:${sz * 0.24}px;background:radial-gradient(#FFF9C4,#FFD54F);border-radius:50%;top:50%;left:50%;transform:translate(-50%,-50%);z-index:2;box-shadow:0 0 ${sz * 0.15}px #FFD54F80"></div></div>`;
    }

    // Organic stem SVG
    const stemLean = randInt(-14, 14);
    const stemCurve = randInt(-18, 18);
    const stemThick = 3 + Math.random() * 2;
    const stemColor = ['#3a8a2a', '#4a9e3a', '#2d7a1f', '#348a34'][
      randInt(0, 3)
    ];
    const stemLight = ['#5aaa4a', '#6bbe5a', '#70c460'][randInt(0, 2)];
    const sx = 20,
      ex = 20 + stemCurve,
      ey = stemH;
    const cp1x = sx + stemLean,
      cp1y = stemH * 0.33,
      cp2x = sx - stemLean + stemCurve,
      cp2y = stemH * 0.66;
    const svgW = 60;
    let stemSVG = `<svg width="${svgW}" height="${stemH + 4}" viewBox="0 0 ${svgW} ${stemH + 4}" style="overflow:visible">`;
    stemSVG += `<path d="M${sx},2 C${cp1x},${cp1y} ${cp2x},${cp2y} ${ex},${ey}" fill="none" stroke="${stemColor}" stroke-width="${stemThick}" stroke-linecap="round"/>`;
    stemSVG += `<path d="M${sx - 0.5},2 C${cp1x - 0.5},${cp1y} ${cp2x - 0.5},${cp2y} ${ex - 0.5},${ey}" fill="none" stroke="${stemLight}" stroke-width="${stemThick * 0.35}" stroke-linecap="round" opacity=".4"/>`;
    const leafCount = 1 + Math.floor(Math.random() * 3);
    for (let l = 0; l < leafCount; l++) {
      const lt = 0.2 + l * 0.22 + Math.random() * 0.08;
      const t = lt,
        t2 = t * t,
        t3 = t2 * t,
        mt = 1 - t,
        mt2 = mt * mt,
        mt3 = mt2 * mt;
      const lx = mt3 * sx + 3 * mt2 * t * cp1x + 3 * mt * t2 * cp2x + t3 * ex;
      const ly = mt3 * 2 + 3 * mt2 * t * cp1y + 3 * mt * t2 * cp2y + t3 * ey;
      const lSide = l % 2 === 0 ? 1 : -1;
      const lLen = 12 + Math.random() * 12;
      const lWid = 5 + Math.random() * 4;
      const lAngle = lSide * (25 + Math.random() * 35);
      const leafG = ['#3a8a2a', '#48a038', '#4a9e3a', '#3d9030'][randInt(0, 3)];
      const leafD = ['#2d6e1f', '#256818'][randInt(0, 1)];
      const tipX = lx + lSide * lLen * Math.cos((lAngle * Math.PI) / 180);
      const tipY =
        ly - lLen * Math.sin((Math.abs(lAngle) * Math.PI) / 180) * 0.3;
      stemSVG += `<path d="M${lx},${ly} Q${lx + lSide * lLen * 0.5},${ly - lWid} ${tipX},${tipY} Q${lx + lSide * lLen * 0.5},${ly + lWid} ${lx},${ly}" fill="${leafG}" opacity=".88" stroke="${leafD}" stroke-width=".4"/>`;
      stemSVG += `<path d="M${lx},${ly} Q${lx + lSide * lLen * 0.45},${ly - lWid * 0.15} ${tipX},${tipY}" fill="none" stroke="${leafD}" stroke-width=".7" opacity=".5"/>`;
      for (let v = 1; v <= 2; v++) {
        const vt = v * 0.3;
        const vx = lx + (tipX - lx) * vt;
        const vy = ly + (tipY - ly) * vt;
        stemSVG += `<line x1="${vx}" y1="${vy}" x2="${vx + lSide * lWid * 0.6}" y2="${vy - lWid * 0.4}" stroke="${leafD}" stroke-width=".4" opacity=".35"/>`;
      }
    }
    stemSVG += `</svg>`;

    const flowerLean = randInt(-6, 6);
    const html = `${headHTML}<div style="display:flex;justify-content:center;margin-top:-2px">${stemSVG}</div><div class="flower-label">${this.escape(word)}</div>`;

    const flower: Flower = {
      id: ++this.idCounter,
      word,
      x: clampedX,
      z: 10 + n,
      rotate: flowerLean,
      anim,
      dur,
      html: this.sanitizer.bypassSecurityTrustHtml(html),
    };
    this.flowers.push(flower);

    // Sparkle burst (8 particles)
    for (let i = 0; i < 8; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 30 + Math.random() * 50;
      const spSz = 4 + Math.random() * 5;
      const spClr = [colorL, color, '#FFF9C4', '#fff'][i % 4];
      const spHtml = `<div style="position:absolute;left:${x + 15}px;bottom:${stemH + 25}px;width:${spSz}px;height:${spSz}px;border-radius:50%;background:${spClr};pointer-events:none;animation:sharp-particle-burst .7s ease forwards;--dx:${(Math.cos(angle) * dist).toFixed(1)}px;--dy:${(Math.sin(angle) * dist).toFixed(1)}px;box-shadow:0 0 6px ${spClr}80"></div>`;
      this.addEphemeral(spHtml, 700);
    }

    // Floating pollen sparkles (>=5 words, every other)
    if (n >= 5 && n % 2 === 0) {
      const count = 4 + Math.floor(n / 3);
      for (let i = 0; i < count; i++) {
        const px = randInt(10, fw - 10);
        const py = randInt(10, fh - 20);
        const pSz = 2 + Math.random() * 4;
        const pColor = ['#FFF9C4', '#fff', color, colorL][i % 4];
        const dur2 = (2 + Math.random() * 2).toFixed(2);
        const html2 = `<div style="position:absolute;left:${px}px;top:${py}px;width:${pSz}px;height:${pSz}px;border-radius:50%;background:${pColor};pointer-events:none;opacity:.7;animation:sharp-butterfly-float ${dur2}s ease forwards;--bx:${randInt(-40, 40)}px;--by:${randInt(-60, -20)}px;box-shadow:0 0 ${pSz + 3}px ${pColor}80"></div>`;
        this.addEphemeral(html2, 4000);
      }
    }

    // Butterflies every 4 words
    if (n > 0 && n % 4 === 0) {
      const count = 1 + Math.floor(n / 8);
      for (let b = 0; b < count; b++) {
        const bx = randInt(-100, 100);
        const by = randInt(-120, -40);
        const bfSz = 16 + Math.random() * 12;
        const dur3 = (2 + Math.random() * 2).toFixed(2);
        const bfHtml = `<div style="position:absolute;left:${randInt(20, fw - 40)}px;bottom:${randInt(40, fh - 20)}px;pointer-events:none;animation:sharp-butterfly-float ${dur3}s ease forwards;--bx:${bx}px;--by:${by}px;z-index:100"><svg viewBox="0 0 24 24" width="${bfSz}" height="${bfSz}"><circle cx="12" cy="14" r="2" fill="#FFD54F" opacity=".8"/><ellipse cx="7" cy="10" rx="5" ry="6" fill="${color}" opacity=".6" transform="rotate(-15 7 10)"/><ellipse cx="17" cy="10" rx="5" ry="6" fill="${colorL}" opacity=".6" transform="rotate(15 17 10)"/><ellipse cx="8" cy="15" rx="3" ry="4" fill="${colorL}" opacity=".4" transform="rotate(-10 8 15)"/><ellipse cx="16" cy="15" rx="3" ry="4" fill="${color}" opacity=".4" transform="rotate(10 16 15)"/></svg></div>`;
        this.addEphemeral(bfHtml, 3500);
      }
    }

    // Progressive background enrichment
    const wordsCount = this.words.length;
    if (wordsCount === 4) {
      this.fieldBackground =
        'linear-gradient(180deg,#e0f5e0 0%,#c8e8c8 50%,#a8d8a8 100%)';
    } else if (wordsCount === 7) {
      this.fieldBackground =
        'linear-gradient(180deg,#d8f0e0 0%,#b8e4c0 40%,#90d4a0 100%)';
      this.fieldBorderColor = '#90c890';
    } else if (wordsCount === 10) {
      this.fieldBackground =
        'linear-gradient(180deg,#d0ecd0 0%,#b0dcb0 40%,#88cc88 100%)';
      this.fieldBorderColor = '#80c080';
      this.fieldBoxShadow = 'inset 0 0 30px rgba(240,200,64,.06)';
    } else if (wordsCount === 14) {
      this.fieldBackground =
        'linear-gradient(180deg,#e8f0d8 0%,#c8e4b0 30%,#a0d890 60%,#80c870 100%)';
      this.fieldBorderColor = '#70b860';
      this.fieldBoxShadow =
        'inset 0 0 50px rgba(240,200,64,.1),0 0 15px rgba(80,180,80,.08)';
      const glowHtml = `<div style="position:absolute;inset:0;pointer-events:none;background:radial-gradient(ellipse at 50% 30%,rgba(255,230,140,.12),transparent 70%);z-index:0;border-radius:inherit"></div>`;
      this.addEphemeral(glowHtml, 999999);
    } else if (wordsCount === 18) {
      this.fieldBackground =
        'linear-gradient(180deg,#d0ecd0 0%,#a0d8a0 30%,#70c870 60%,#50b850 90%,#40a040 100%)';
      this.fieldBoxShadow =
        'inset 0 0 60px rgba(255,215,0,.14),0 0 25px rgba(80,180,80,.12)';
      const glow2Html = `<div style="position:absolute;inset:0;pointer-events:none;background:radial-gradient(ellipse at 30% 60%,rgba(255,100,200,.06),transparent 60%),radial-gradient(ellipse at 70% 40%,rgba(140,100,255,.06),transparent 60%);z-index:0;border-radius:inherit"></div>`;
      this.addEphemeral(glow2Html, 999999);
    }
  }

  private addEphemeral(html: string, lifetimeMs: number): void {
    const id = ++this.idCounter;
    this.ephemerals.push({
      id,
      html: this.sanitizer.bypassSecurityTrustHtml(html),
    });
    if (lifetimeMs < 999999) {
      const t = setTimeout(() => {
        this.ephemerals = this.ephemerals.filter((e) => e.id !== id);
      }, lifetimeMs);
      this.ephemeralCleanup.push(t);
    }
  }

  private escape(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ============================
  // DEMO (intro screen)
  // ============================
  private runDemo(): void {
    if (this.phase !== 'intro') return;
    this.demoRunning = true;
    const examples: Record<string, string[]> =
      this.translation.getCurrentLanguage() === 'he'
        ? {
            Animals: ['חתול', 'כלב', 'אריה'],
            Food: ['תפוח', 'לחם', 'גבינה'],
            'Household Objects': ['כיסא', 'מנורה', 'כוס'],
          }
        : {
            Animals: ['cat', 'dog', 'lion'],
            Food: ['apple', 'bread', 'cheese'],
            'Household Objects': ['chair', 'lamp', 'cup'],
          };
    const words =
      examples[this.category as string] || examples['Animals'];
    let idx = 0;

    const startTypingNext = () => {
      if (!this.demoRunning || this.phase !== 'intro') return;
      if (idx >= words.length) {
        this.demoExplainVisible = false;
        const t = setTimeout(() => {
          if (!this.demoRunning) return;
          this.demoFlowers = [];
          idx = 0;
          const t2 = setTimeout(() => startTypingNext(), 900);
          this.demoTimers.push(t2);
        }, 2800);
        this.demoTimers.push(t);
        return;
      }
      if (idx === 0) this.demoExplainVisible = true;
      const word = words[idx];
      this.demoInputText = '';
      let ci = 0;
      const typeInt = setInterval(() => {
        if (!this.demoRunning) {
          clearInterval(typeInt);
          return;
        }
        ci++;
        this.demoInputText = word.substring(0, ci);
        if (ci >= word.length) {
          clearInterval(typeInt);
          const flashT = setTimeout(() => {
            if (!this.demoRunning) return;
            this.demoInputFlash = true;
            setTimeout(() => (this.demoInputFlash = false), 200);
            this.demoInputText = '';
            this.makeDemoFlower(word, idx);
            idx++;
            // Slower pacing so each example word is readable before the next.
            const next = setTimeout(() => startTypingNext(), 1100);
            this.demoTimers.push(next);
          }, 350);
          this.demoTimers.push(flashT);
        }
      }, 160);
      this.demoTimers.push(typeInt);
    };
    startTypingNext();
  }

  private makeDemoFlower(word: string, i: number): void {
    const field = this.demoFieldRef?.nativeElement;
    if (!field) return;
    const fw = field.offsetWidth;
    const demoPals = [
      { base: '#FF1493', light: '#FF6EB4', dark: '#C71585' },
      { base: '#6C3CE0', light: '#A07BFF', dark: '#4400AA' },
      { base: '#FF9800', light: '#FFCC80', dark: '#E65100' },
    ];
    const spacing = fw / (3 + 1);
    const x = spacing * (i + 1) - 18;
    const pal = demoPals[i % 3];
    const stemH = [35, 50, 30][i % 3];
    const sz = [22, 26, 20][i % 3];
    const types = ['peony', 'tulip', 'poppy'];
    const type = types[i % 3];
    const rotate = [-2, 3, -1][i % 3];
    let headHTML = '';
    const glow = `filter:drop-shadow(0 0 ${sz * 0.25}px ${pal.base}80)`;
    const sway = `animation:sharp-petal-sway ${(2 + i * 0.5).toFixed(1)}s ease-in-out infinite;`;

    if (type === 'peony') {
      let p = '';
      for (let l = 0; l < 3; l++) {
        const pc = 4 + l * 2;
        for (let j = 0; j < pc; j++) {
          const a = j * (360 / pc) + l * 12;
          const lsz = sz * (0.3 + l * 0.17);
          const clr = l < 2 ? pal.base : pal.light;
          p += `<div style="position:absolute;width:${lsz * 0.4}px;height:${lsz * 0.58}px;background:linear-gradient(${a}deg,${clr},${pal.light});border-radius:50%;top:50%;left:50%;transform:translate(-50%,-${55 + l * 11}%) rotate(${a}deg);transform-origin:center bottom;opacity:${(0.65 + l * 0.1).toFixed(1)}"></div>`;
        }
      }
      headHTML = `<div style="position:relative;width:${sz * 1.5}px;height:${sz * 1.5}px;${sway}${glow}">${p}<div style="position:absolute;width:${sz * 0.2}px;height:${sz * 0.2}px;background:radial-gradient(#FFF9C4,#FFD54F);border-radius:50%;top:48%;left:50%;transform:translate(-50%,-50%);z-index:5"></div></div>`;
    } else if (type === 'tulip') {
      const h = sz * 1.1;
      const w = sz * 0.55;
      headHTML = `<div style="${sway}${glow}"><div style="width:${w}px;height:${h}px;background:linear-gradient(160deg,${pal.dark},${pal.base},${pal.light});border-radius:${w * 0.5}px ${w * 0.5}px ${w * 0.1}px ${w * 0.1}px;box-shadow:inset ${w * 0.08}px 0 ${w * 0.12}px rgba(255,255,255,.2)"></div></div>`;
    } else {
      let p = '';
      const pc = 5;
      for (let j = 0; j < pc; j++) {
        const a = j * (360 / pc);
        p += `<div style="position:absolute;width:${sz * 0.4}px;height:${sz * 0.5}px;background:radial-gradient(${pal.light} 10%,${pal.base} 60%,${pal.dark});border-radius:50%;top:50%;left:50%;transform:translate(-50%,-95%) rotate(${a}deg);transform-origin:center bottom"></div>`;
      }
      headHTML = `<div style="position:relative;width:${sz * 1.4}px;height:${sz * 1.4}px;${sway}${glow}">${p}<div style="position:absolute;width:${sz * 0.22}px;height:${sz * 0.22}px;background:radial-gradient(#333,#1a1a1a);border-radius:50%;top:50%;left:50%;transform:translate(-50%,-50%);z-index:1"></div></div>`;
    }

    const lean = [-4, 5, -3][i % 3];
    const curve = [3, -4, 5][i % 3];
    const stemSvg = `<svg width="30" height="${stemH + 2}" viewBox="0 0 30 ${stemH + 2}" style="overflow:visible"><path d="M15,1 C${15 + lean},${stemH * 0.35} ${15 + curve},${stemH * 0.65} ${15 + curve},${stemH}" fill="none" stroke="#4a9e3a" stroke-width="2.5" stroke-linecap="round"/><path d="M${15 + lean * 0.3},${stemH * 0.4} Q${15 + (i % 2 ? 7 : -7)},${stemH * 0.35 - 3} ${15 + (i % 2 ? 12 : -12)},${stemH * 0.38} Q${15 + (i % 2 ? 7 : -7)},${stemH * 0.35 + 3} ${15 + lean * 0.3},${stemH * 0.4}" fill="#48a038" opacity=".85" stroke="#2d6e1f" stroke-width=".3"/></svg>`;
    const html = `${headHTML}<div style="display:flex;justify-content:center;margin-top:-1px">${stemSvg}</div><div class="flower-label">${this.escape(word)}</div>`;

    this.demoFlowers.push({
      id: ++this.idCounter,
      word,
      x,
      z: 10 + i,
      rotate,
      anim: 'sharp-bloom-pop',
      dur: '0.50',
      html: this.sanitizer.bypassSecurityTrustHtml(html),
    });
  }

  // ============================
  // FINISH
  // ============================
  private finish(): void {
    this.phase = 'done';
    this.audio.success();
    const uniqueValid = this.words.length;
    // Low-output flag (handoff 2026-08-17): fewer than 5 words. Typing itself is
    // part of the measurement here, so this is a quality flag, not a score.
    if (this.interruptions) {
      this.state.addValidityFlag(`Word task paused: ${this.interruptions}`);
    }
    if (uniqueValid < 5) {
      this.state.addValidityFlag('Low word output');
    }
    const ts = this.timestamps;
    const firstLatency = ts.length ? this.scoring.round(ts[0], 0) : 25000;
    let meanInterval = 0;
    if (ts.length > 1) {
      const intervals: number[] = [];
      for (let i = 1; i < ts.length; i++) intervals.push(ts[i] - ts[i - 1]);
      meanInterval = this.scoring.round(this.scoring.mean(intervals), 0);
    }
    let clustering = 0;
    if (this.category === 'Animals' && this.words.length > 1) {
      let switches = 0;
      let last: string | null = null;
      for (const w of this.words) {
        let c: string | null = null;
        for (const [k, v] of Object.entries(ANIMAL_CLUSTERS)) {
          if (v.includes(w)) {
            c = k;
            break;
          }
        }
        if (last !== null && c !== last) switches++;
        last = c;
      }
      clustering = switches;
    }
    if (ts.length >= 3) {
      const ints: number[] = [];
      for (let i = 1; i < ts.length; i++) ints.push(ts[i] - ts[i - 1]);
      if (ints.length > 2 && this.scoring.stddev(ints) < 200) {
        this.state.addValidityFlag('Uniform word timing');
      }
    }
    const response = {
      category: this.category,
      words: this.words,
      timestamps: ts,
      uniqueValid,
      repetitions: this.repetitions,
      firstLatency,
      meanInterval,
      clustering,
    };
    const result: TestResult = {
      testId: this.testId,
      response,
      correct: uniqueValid >= 10,
      responseTime: Date.now() - this.wallStart,
      timestamp: Date.now(),
    };
    this.testComplete.emit(result);
  }
}
